import {Col, Grid, Row} from "xps-react";
import React, {ChangeEvent, useEffect, useMemo} from "react";
import {EstateFlowChartDetails, Page, PageName, ReportConfig, ReportSection, ReportType,} from "../models/Reporting";
import {getReportSections, initialSelectedPagesForAnnualReport} from "../ReportingConstants";
import ReportSetupHeader from "./ReportSetupHeader";
import {Button, Icon} from "../../components";
import ReportSetupPagesSection from "./ReportSetupPagesSection";
import {
    selectAdviceLibraryDocuments,
    selectIsReportGenerationTriggered,
    selectPracticeDocuments,
    selectSelectedClientDocuments,
    selectSelectedMyDocuments,
    selectSelectedPagesForReport,
    selectSelectedReportType,
    selectShowDrawerForAdviceLibrary,
    selectShowDrawerForClientDocuments,
    selectShowDrawerForMyDocuments,
    selectShowDrawerForPracticeDocuments,
    setSelectedAdviceLibraryDocuments,
    setSelectedClientDocuments,
    setSelectedMyDocuments,
    setSelectedPagesForAnnualReport,
    setSelectedPracticeDocuments,
    setSelectedReportType,
    setShowDrawerForAdviceLibrary,
    setShowDrawerForClientDocuments,
    setShowDrawerForMyDocuments,
    setShowDrawerForPracticeDocuments
} from "./ReportingSlice";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {
    selectAdviceLibrarySubMenus,
    selectPracticeDocumentsSubMenus,
    setAdviceLibraryMenu,
    setPracticeDocumentsMenu
} from "../../Resources/menuStructureSlice";
import AdvisorDocumentSetupSection from "./AdvisorDocumentSetupSection";
import {
    ClientMeetingDocumentItem,
    ClientMeetingDocumentsResponse,
    ClientMeetingFolderItem
} from "src/Resources/models/ClientDocuments";
import DocumentSetupSection from "./DocumentSetupSection";
import {
    selectApprovedProfileFromApi,
    selectClientDocuments,
    selectMyDocuments,
    setClientDocumentsResponse,
    setMyDocumentsResponse
} from "src/Resources/clientMeetingDocumentSlice";
import SelectedDocumentsSection from "./SelectedDocumentsSection";
import {useMsal} from "@azure/msal-react";
import {msalUtils} from "../../MsalUtils";
import {resourcesApiClient} from "../../Resources/ResourcesApiClient";
import {emptyClientMeetingDocumentsSubMenusData} from "../../Resources/ResourcesConstant";
import {selectProfile, selectResourceCode} from "../../ClientManagement/ClientProfile/activeProfileSlice";
import {reportingApiClient} from "../ReportingApiClient";
import {MenuStructureResponse} from "../../Resources/models/AdviceLibrary";
import {selectReleaseToggles} from "../../ReleaseToggles/releaseTogglesSlice";
import {EstateFlowchartState} from "../../EstateFlowchart/model/EstateFlowchartTypes";

export type ReportSetupProps = {
    navigateToEditReportPage: (param: string) => void;
    estateFlowCharts: EstateFlowchartState[]
};

function DocumentSummarySection(props: { documentName: string, onDelete: () => void }) {
    return <div

        aria-label={`Document ${props.documentName}`}
        className="document-summary-item"
    >
        <div className="document-summary-item__left-content">
            <Icon className="marginleft-12 marginright-12" name="file_outline"/>
            <span className="document-summary-item__name">{props.documentName}</span>
        </div>
        <Button
            onClick={props.onDelete}
            kind="borderless"
            icon="only"
            size="small"
            iconName={"delete"}
        />
    </div>;
}

function setSortOrderForFiles(documentItems: ClientMeetingDocumentItem[], sortOrder: any, index: number) {
    documentItems.forEach(document => {
        sortOrder[document.id] = index;
        ++index;
    })
    return index;
}

function setSortOrderForFolders(folderItems: ClientMeetingFolderItem[], sortOrder: any, index: number) {
    folderItems.forEach(folderItem => {
        index = setSortOrderForFiles(folderItem.driveItems, sortOrder, index);
        index = setSortOrderForFolders(folderItem.folderItems, sortOrder, index);
    })
    return index;
}

function getSortOrderOfClientDocuments(clientMeetingDocumentsSubMenu: ClientMeetingDocumentsResponse) {
    const sortOrder: any = {};
    let index = 0;
    index = setSortOrderForFiles(clientMeetingDocumentsSubMenu.documentItems, sortOrder, index);
    setSortOrderForFolders(clientMeetingDocumentsSubMenu.folderItems, sortOrder, index);
    return sortOrder;
}

const GreyedOutPages = ['assetsSummaryDetailed', 'goalsSummaryDetailed', 'familyTree', 'currentVsProposed', 'reviewPortfolioReserve', 'assetReliance'];

const ReportSetup: React.FC<ReportSetupProps> = ({
                                                     navigateToEditReportPage,
                                                     estateFlowCharts
                                                 }) => {
    const dispatch = useAppDispatch();
    const selectedPagesForAnnualReportFromRedux = useAppSelector(selectSelectedPagesForReport);
    const selectedPagesForAnnualReport: ReportConfig = JSON.parse(JSON.stringify(selectedPagesForAnnualReportFromRedux));

    const clientDocumentsSubMenus = useAppSelector(selectClientDocuments);

    const profileResponse = useAppSelector(selectProfile);

    const selectedClientDocuments = useAppSelector(selectSelectedClientDocuments);
    const openClientDocumentsDrawer = useAppSelector(selectShowDrawerForClientDocuments);

    const myDocumentsSubMenus = useAppSelector(selectMyDocuments);
    const selectedMyDocuments = useAppSelector(selectSelectedMyDocuments);
    const openMyDocumentsDrawer = useAppSelector(selectShowDrawerForMyDocuments);

    const adviceLibrarySubMenus = useAppSelector(selectAdviceLibrarySubMenus);
    const selectedAdviceLibraryDocuments = useAppSelector(selectAdviceLibraryDocuments);
    const openAdviceLibraryDrawer = useAppSelector(selectShowDrawerForAdviceLibrary);

    const openPracticeDocumentsDrawer = useAppSelector(selectShowDrawerForPracticeDocuments);
    const selectedPracticeDocuments = useAppSelector(selectPracticeDocuments);
    const practiceDocumentsSubMenus = useAppSelector(selectPracticeDocumentsSubMenus);
    const approvedProfileFromApi = useAppSelector(selectApprovedProfileFromApi);
    const msal = useMsal();
    const currentUserId = msalUtils.getLanId(msal);
    const resourceCode = useAppSelector(selectResourceCode);
    const initialReportType = useAppSelector(selectSelectedReportType);
    const releaseToggles = useAppSelector(selectReleaseToggles);
    const isReportGenerationTriggered = useAppSelector(selectIsReportGenerationTriggered);


    useEffect(() => {
        dispatch(setShowDrawerForAdviceLibrary(false));
        if (profileResponse?.id && isReportGenerationTriggered === false) {
            reportingApiClient.deleteClientReport(profileResponse.id);
        }
    }, [dispatch]);

    const defaultAdviceLibrarySortOrderMap: any = {};
    const defaultPracticeDocumentsSortOrderMap: any = {};
    const defaultClientDocumentsSortOrderMap: any = useMemo(
        () => getSortOrderOfClientDocuments(clientDocumentsSubMenus),
        [clientDocumentsSubMenus]
    );
    const defaultMyDocumentsSortOrderMap: any = useMemo(
        () => getSortOrderOfClientDocuments(myDocumentsSubMenus),
        [myDocumentsSubMenus]
    );

    let index = 0;
    adviceLibrarySubMenus.advisorDocumentSubMenus.forEach(submenu => {
        submenu.subMenuItems.forEach((item) => {
            defaultAdviceLibrarySortOrderMap[item.documentLabel] = index;
            index++;
        })
    })

    practiceDocumentsSubMenus.advisorDocumentSubMenus.forEach(submenu => {
        submenu.subMenuItems.forEach((item) => {
            defaultPracticeDocumentsSortOrderMap[item.documentLabel] = index;
            index++;
        })
    })

    const toggleCheckbox = (e: ChangeEvent<HTMLInputElement>) => {
        const {name, checked} = e.target;
        if(name.trim() === "estateFlowChart" && estateFlowCharts)
        {
            let updatedItems: EstateFlowChartDetails [] = [];

            estateFlowCharts.forEach(ele => {
                updatedItems.push({
                    flowchartId: ele.flowchartId!,
                    flowchartName: ele.flowchartName!,
                    isEnabled: checked
                })
            })

            const selectedPagesConfig: ReportConfig = {
                ...selectedPagesForAnnualReport,
                ["estateFlowChart"]: {
                    isEnabled: checked,
                    estateFlowChartDetails: [...updatedItems]
                },
            };
            dispatch(setSelectedPagesForAnnualReport(selectedPagesConfig));
        }
        else if (estateFlowCharts && estateFlowCharts.some(ele => ele.flowchartName!.trim() === name.trim())) {
            let updatedItems: EstateFlowChartDetails [] = [];
            let isAnyEstateFlowChartSelected = false;
            if (selectedPagesForAnnualReport.estateFlowChart && selectedPagesForAnnualReport.estateFlowChart.estateFlowChartDetails && selectedPagesForAnnualReport.estateFlowChart.estateFlowChartDetails.length > 0) {
                if (selectedPagesForAnnualReport.estateFlowChart.estateFlowChartDetails.some(ele => ele.flowchartName.trim() === name.trim())) {
                    updatedItems = selectedPagesForAnnualReport.estateFlowChart.estateFlowChartDetails.map((flowchart, index) => {
                        if (flowchart.flowchartName === name) {
                            return {
                                ...flowchart,
                                isEnabled: checked
                            };
                        }
                        return flowchart;
                    });
                } else {
                    updatedItems = [...selectedPagesForAnnualReport.estateFlowChart.estateFlowChartDetails]
                    updatedItems = [...pushEstateFlowChartData(estateFlowCharts,name,updatedItems,checked)]
                }
            } else {
                updatedItems = [...pushEstateFlowChartData(estateFlowCharts,name,updatedItems,checked)]
            }

            isAnyEstateFlowChartSelected = updatedItems.some(ele => ele.isEnabled === true);

            const selectedPagesConfig: ReportConfig = {
                ...selectedPagesForAnnualReport,
                ["estateFlowChart"]: {
                    isEnabled: isAnyEstateFlowChartSelected,
                    estateFlowChartDetails: [...updatedItems]
                },
            };
            dispatch(setSelectedPagesForAnnualReport(selectedPagesConfig));
        }
        else {
            const selectedPagesConfig: ReportConfig = {
                ...selectedPagesForAnnualReport,
                [name]: {
                    isEnabled: checked,
                },
            };
            dispatch(setSelectedPagesForAnnualReport(selectedPagesConfig));
        }
    };

    const pushEstateFlowChartData = (estateFlowChart : EstateFlowchartState[], name:string,updatedItems: EstateFlowChartDetails [],checked:boolean) => {
        let estateFlowchartState =  estateFlowChart.find(ele => ele.flowchartName!.trim() === name.trim())!
        updatedItems.push({
            flowchartId: estateFlowchartState.flowchartId!,
            flowchartName: estateFlowchartState.flowchartName!,
            isEnabled: checked
        })
        return updatedItems
    }

    const isAnyPageCheckedInSection = (pageSection: ReportSection) => {
        const pagesList = pageSection.pagesList as PageName[];
        return pagesList.some(
            (page) => selectedPagesForAnnualReport[page]?.isEnabled
                && (GreyedOutPages.indexOf(page) === -1 || initialReportType === 'Other Report')
        );
    };

    const toggleAllPagesInSection = (
        pageSection: ReportSection,
        checked: boolean
    ) => {
        // if annual report then only below shud be called...
        const pages = pageSection.pages.filter((page) => !page.isDisabled && !page.isGreyedOut);
        if(pageSection.sectionTitle === 'Wealth Planning' && estateFlowCharts)
        {
            const selectedPageList = pages.reduce(
                (pagesAccumulator, page) => ({
                    ...pagesAccumulator,
                    [page.value]: {
                        isEnabled: checked,
                    },
                }),
                {}
            );
            let updatedItems: EstateFlowChartDetails [] = [];

            estateFlowCharts.forEach(ele => {
                updatedItems.push({
                    flowchartId: ele.flowchartId!,
                    flowchartName: ele.flowchartName!,
                    isEnabled: checked
                })
            })

            dispatch(setSelectedPagesForAnnualReport({
                ...selectedPagesForAnnualReport,
                ...selectedPageList,
                ["estateFlowChart"]: {
                    isEnabled: checked,
                    estateFlowChartDetails: [...updatedItems]
                },
            }));
        }
        else
        {
            const selectedPageList = pages.reduce(
                (pagesAccumulator, page) => ({
                    ...pagesAccumulator,
                    [page.value]: {
                        isEnabled: checked,
                    },
                }),
                {}
            );
            dispatch(setSelectedPagesForAnnualReport({
                ...selectedPagesForAnnualReport,
                ...selectedPageList,
            }));
        }

    };

    const onChangeReportType = (e: ChangeEvent<HTMLInputElement>) => {
        const {value} = e.target;
        dispatch(setSelectedReportType(value as ReportType));

        let updatedItems: EstateFlowChartDetails [] = [];
        if(estateFlowCharts && estateFlowCharts.length > 0)
        {
            estateFlowCharts.forEach(ele => {
                updatedItems.push({
                    flowchartId: ele.flowchartId!,
                    flowchartName: ele.flowchartName!,
                    isEnabled: false
                })
            })
        }

        if (value === "Annual Report") {
            dispatch(setSelectedPagesForAnnualReport({...initialSelectedPagesForAnnualReport,
                ["estateFlowChart"]: {
                isEnabled: false,
                    estateFlowChartDetails: [...updatedItems]
            }}))
        } else {
            Object.keys(selectedPagesForAnnualReport).forEach((pageName) => {
                selectedPagesForAnnualReport[pageName as PageName] = {
                    isEnabled: false,
                };
            });
            dispatch(setSelectedPagesForAnnualReport({
                ...selectedPagesForAnnualReport,
                ["estateFlowChart"]: {
                    isEnabled: false,
                    estateFlowChartDetails: [...updatedItems]
                },
            }));
        }
    };

    function isChecked(page: Page) {
        const isPageEnabled = !page.isDisabled;
        let isPageSelected: boolean;
        if (selectedPagesForAnnualReport.estateFlowChart && selectedPagesForAnnualReport.estateFlowChart.estateFlowChartDetails && selectedPagesForAnnualReport.estateFlowChart.estateFlowChartDetails.length > 0) {
            if (selectedPagesForAnnualReport.estateFlowChart.estateFlowChartDetails.some(ele => ele.flowchartName.trim() === page.value.trim())) {

                let estateFlowChartDetails = selectedPagesForAnnualReport.estateFlowChart.estateFlowChartDetails.find(ele => ele.flowchartName.trim() === page.value.trim())
                isPageSelected = estateFlowChartDetails?.isEnabled!
            } else {
                isPageSelected =
                    selectedPagesForAnnualReport[page.value as PageName]?.isEnabled;
            }
        } else {
            isPageSelected =
                selectedPagesForAnnualReport[page.value as PageName]?.isEnabled;
        }

        return isPageEnabled && isPageSelected!;
    }

    const handleAttachClientDocuments = () => {
        const documentNames = [...selectedClientDocuments];
        documentNames.sort((document1, document2) =>
            defaultClientDocumentsSortOrderMap[document1.id] - defaultClientDocumentsSortOrderMap[document2.id]);

        dispatch(setSelectedClientDocuments(documentNames));
        dispatch(setShowDrawerForClientDocuments(false));
    };

    const handleAttachMyDocuments = () => {
        const documentNames = [...selectedMyDocuments];
        documentNames.sort((document1, document2) =>
            defaultMyDocumentsSortOrderMap[document1.id] - defaultMyDocumentsSortOrderMap[document2.id]);

        dispatch(setSelectedMyDocuments(documentNames));
        dispatch(setShowDrawerForMyDocuments(false));
    };

    const handleAttachAdviceLibraryDocuments = () => {
        const documentNames = [...selectedAdviceLibraryDocuments];
        documentNames.sort((document1, document2) =>
            defaultAdviceLibrarySortOrderMap[document1] - defaultAdviceLibrarySortOrderMap[document2]);

        dispatch(setSelectedAdviceLibraryDocuments(documentNames));
        dispatch(setShowDrawerForAdviceLibrary(false));
    };

    const handleAttachPracticeDocuments = () => {
        const documentNames = [...selectedPracticeDocuments];
        documentNames.sort((document1, document2) =>
            defaultPracticeDocumentsSortOrderMap[document1] - defaultPracticeDocumentsSortOrderMap[document2]);

        dispatch(setSelectedPracticeDocuments(documentNames));
        dispatch(setShowDrawerForPracticeDocuments(false));
    };

    const deleteAdviceLibraryDocument = (deletedDocumentName: string) => {
        const updatedDocumentNames = selectedAdviceLibraryDocuments.filter(
            (documentName) => documentName !== deletedDocumentName
        );
        dispatch(setSelectedAdviceLibraryDocuments(updatedDocumentNames));
    };

    const deletePracticeDocument = (deletedDocumentName: string) => {
        const updatedDocumentNames = selectedPracticeDocuments.filter(
            (documentName) => documentName !== deletedDocumentName
        );
        dispatch(setSelectedPracticeDocuments(updatedDocumentNames));
    };

    const deleteClientDocument = (deletedDocumentId: string) => {
        const updatedDocumentNames = selectedClientDocuments.filter(
            (document) => document.id !== deletedDocumentId
        );
        dispatch(setSelectedClientDocuments(updatedDocumentNames));
    };

    const deleteMyDocument = (deletedDocumentId: string) => {
        const updatedDocumentNames = selectedMyDocuments.filter(
            (document) => document.id !== deletedDocumentId
        );
        dispatch(setSelectedMyDocuments(updatedDocumentNames));
    };

    function saveSelectedAdviceLibraryDocuments(updatedDocumentNames: string[]) {
        dispatch(setSelectedAdviceLibraryDocuments(updatedDocumentNames));
    }

    function saveShowDrawerForAdviceLibraryDocuments(showOrHide: boolean) {
        dispatch(setShowDrawerForAdviceLibrary(showOrHide));
    }

    function saveSelectedPracticeDocuments(updatedDocumentNames: string[]) {
        dispatch(setSelectedPracticeDocuments(updatedDocumentNames));
    }

    function saveShowDrawerForPracticeDocuments(showOrHide: boolean) {
        dispatch(setShowDrawerForPracticeDocuments(showOrHide));
    }

    const isArchived: boolean = profileResponse.archived || !profileResponse.activeClient;
    const reportSections = getReportSections(initialReportType,
        profileResponse.activePortfolioReserve,
        isArchived,
        releaseToggles?.enableEstateFlowchart,
        estateFlowCharts,
        releaseToggles?.enable2026SunsetExemption
    );

    const onRefreshSharePointDocs = () => {

        const clientName = (approvedProfileFromApi.primaryContact.firstName).concat(" ", approvedProfileFromApi.primaryContact.lastName);
        resourcesApiClient.getClientMeetingDocuments(approvedProfileFromApi.id, clientName, currentUserId, resourceCode, "true").then((clientMeetingDocumentsResponse: ClientMeetingDocumentsResponse) => {
            dispatch(setClientDocumentsResponse({...clientMeetingDocumentsResponse, isSharePointDown: false}));
        }).catch((_error) => {
            dispatch(setClientDocumentsResponse({...emptyClientMeetingDocumentsSubMenusData, isSharePointDown: true}));
        });

        resourcesApiClient.getMyDocuments(currentUserId, approvedProfileFromApi.id).then((clientMeetingDocumentsResponse: ClientMeetingDocumentsResponse) => {
            dispatch(setMyDocumentsResponse({...clientMeetingDocumentsResponse, isSharePointDown: false}));
        }).catch((_error) => {
            dispatch(setMyDocumentsResponse({...emptyClientMeetingDocumentsSubMenusData, isSharePointDown: true}));
        });
    }

    const onRefreshSeismicDocs = () => {
        resourcesApiClient.getMenuStructure(true)
            .then((menuStructureResponse: MenuStructureResponse) => {
                dispatch(setAdviceLibraryMenu(menuStructureResponse.menu["AdviceLibrary"]));
                dispatch(setPracticeDocumentsMenu(menuStructureResponse.menu["PracticeDocuments"]));
            })
            .catch(console.error);
    }


    return (
        <div className="report-setup">
            <section className="report-setup__config">
                <Grid>
                    <ReportSetupHeader
                        onChangeReportType={onChangeReportType}
                        selectedReportType={initialReportType}
                    />
                </Grid>
            </section>
            <section className="report-setup__content">
                <div className="report-setup__content__title marginleft-xxxl marginbottom-xl">
                    Content to Include
                </div>
                <Grid>
                    <Row>
                        {reportSections.map((pageColumn) => (
                            <Col
                                key={pageColumn.columnNumber}
                                className="display-flex flex-column"
                                xs={4}
                            >
                                {pageColumn.sections
                                    .filter((pageSection) => !pageSection.isDisabled)
                                    .map((pageSection) => (
                                        <ReportSetupPagesSection
                                            key={pageSection.sectionTitle}
                                            pageSection={pageSection}
                                            isHeaderChecked={isAnyPageCheckedInSection(pageSection)}
                                            toggleSectionHeader={(
                                                e: ChangeEvent<HTMLInputElement>
                                            ) => {
                                                const {checked} = e.target;
                                                toggleAllPagesInSection(pageSection, checked);
                                            }}
                                            isPageChecked={isChecked}
                                            toggleCheckbox={toggleCheckbox}
                                            navigateToEditReportPage={navigateToEditReportPage}
                                        />
                                    ))}
                            </Col>
                        ))}
                        <Col className="display-flex flex-column"></Col>
                    </Row>
                </Grid>
                <Grid className="advisor-documents-container">
                    <Row className="advisor-document-row">
                        {<SelectedDocumentsSection
                            title="Client Documents"
                            onAttach={() => dispatch(setShowDrawerForClientDocuments(true))}
                        >
                            {selectedClientDocuments.map((document: ClientMeetingDocumentItem) => (
                                <DocumentSummarySection
                                    key={document.name}
                                    documentName={document.name}
                                    onDelete={() => deleteClientDocument(document.id)}
                                />
                            ))}
                        </SelectedDocumentsSection>}

                        <SelectedDocumentsSection
                            title="Advice Library"
                            onAttach={() => dispatch(setShowDrawerForAdviceLibrary(true))}
                        >
                            {selectedAdviceLibraryDocuments.map((documentName: string) => (
                                <DocumentSummarySection
                                    key={documentName}
                                    documentName={documentName}
                                    onDelete={() => deleteAdviceLibraryDocument(documentName)}
                                />
                            ))}
                        </SelectedDocumentsSection>

                        {<SelectedDocumentsSection
                            title="My Documents"
                            onAttach={() => dispatch(setShowDrawerForMyDocuments(true))}
                        >
                            {selectedMyDocuments.map((document: ClientMeetingDocumentItem) => (
                                <DocumentSummarySection
                                    key={document.name}
                                    documentName={document.name}
                                    onDelete={() => deleteMyDocument(document.id)}
                                />
                            ))}
                        </SelectedDocumentsSection>}

                        <SelectedDocumentsSection
                            title="Practice Documents"
                            onAttach={() => dispatch(setShowDrawerForPracticeDocuments(true))}
                        >
                            {selectedPracticeDocuments.map((documentName: string) => (
                                <DocumentSummarySection
                                    key={documentName}
                                    documentName={documentName}
                                    onDelete={() => deletePracticeDocument(documentName)}
                                />
                            ))}
                        </SelectedDocumentsSection>
                    </Row>
                </Grid>
            </section>

            <DocumentSetupSection
                drawerLabel={"Client Documents"}
                documentsSubMenus={clientDocumentsSubMenus}
                onAttach={handleAttachClientDocuments}
                openDocumentDrawer={openClientDocumentsDrawer}
                selectedDocuments={selectedClientDocuments}
                setSelectedDocuments={(updatedDocumentNames: ClientMeetingDocumentItem[]) => dispatch(setSelectedClientDocuments(updatedDocumentNames))}
                setShowDrawerForDocument={(showOrHide: boolean) => dispatch(setShowDrawerForClientDocuments(showOrHide))}
                onRefreshSharePointDocs={onRefreshSharePointDocs}
            />

            <DocumentSetupSection
                drawerLabel={"My Documents"}
                documentsSubMenus={myDocumentsSubMenus}
                onAttach={handleAttachMyDocuments}
                openDocumentDrawer={openMyDocumentsDrawer}
                selectedDocuments={selectedMyDocuments}
                setSelectedDocuments={(updatedDocumentNames: ClientMeetingDocumentItem[]) => dispatch(setSelectedMyDocuments(updatedDocumentNames))}
                setShowDrawerForDocument={(showOrHide: boolean) => dispatch(setShowDrawerForMyDocuments(showOrHide))}
                onRefreshSharePointDocs={onRefreshSharePointDocs}
            />

            <AdvisorDocumentSetupSection
                drawerLabel={"Advice Library"}
                advisorDocumentsSubMenuArray={adviceLibrarySubMenus.advisorDocumentSubMenus}
                onAttach={handleAttachAdviceLibraryDocuments}
                openAdvisorDocumentDrawer={openAdviceLibraryDrawer}
                selectedAdvisorDocuments={selectedAdviceLibraryDocuments}
                setSelectedAdvisorDocuments={saveSelectedAdviceLibraryDocuments}
                setShowDrawerForAdvisorDocument={saveShowDrawerForAdviceLibraryDocuments}
                onRefreshSeismicDocs={onRefreshSeismicDocs}
            />

            <AdvisorDocumentSetupSection
                drawerLabel={"Practice Documents"}
                advisorDocumentsSubMenuArray={practiceDocumentsSubMenus.advisorDocumentSubMenus}
                onAttach={handleAttachPracticeDocuments}
                openAdvisorDocumentDrawer={openPracticeDocumentsDrawer}
                selectedAdvisorDocuments={selectedPracticeDocuments}
                setSelectedAdvisorDocuments={saveSelectedPracticeDocuments}
                setShowDrawerForAdvisorDocument={saveShowDrawerForPracticeDocuments}
                onRefreshSeismicDocs={onRefreshSeismicDocs}
            />
        </div>
    );
};

export default ReportSetup;
